import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  UntypedFormControl,
  ValidationErrors,
  Validator
} from '@angular/forms';
import {takeUntil} from 'rxjs/operators';

import {Destroyable} from '../../destroyable.component';
import {LocalizationService} from '../localization/localization.service';
import {isPresent} from '../utils/lang';

@Component({
  selector: 'number-input, number-input[ngModel]',
  template: `
    <input
      #input
      (keypress)="handleNumpadDecimal($event)"
      [ngClass]="classes"
      [ngStyle]="style"
      [class.gp-error]="!isValid()"
      [value]="number"
      [(ngModel)]="moduleNumber"
      (ngModelChange)="onChange()"
      [textMask]="mask"
      [disabled]="readOnly"
      (focus)="onFocus()"
      (blur)="onBlur()"/>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NumberInputComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => NumberInputComponent),
      multi: true
    }]
})
export class NumberInputComponent extends Destroyable implements ControlValueAccessor, Validator, OnInit {

  @ViewChild('input', {static: true})
  public input: ElementRef;

  @Input()
  public mask;

  @Input()
  public type: 'decimal' | 'integer';

  @Input()
  public maxDigits: number;

  @Input()
  public classes: string[] = ['form-control'];

  @Input()
  public style;

  @Input()
  public validOnPristine: boolean = true;

  @Input()
  public formControl: UntypedFormControl;

  @Input()
  public readOnly: boolean;

  @Input()
  public minValue: number;

  @Input()
  public maxValue: number;

  @Output()
  public focus: EventEmitter<void> = new EventEmitter<void>();

  @Output()
  public blur: EventEmitter<void> = new EventEmitter<void>();

  private _number;
  private normalNumber;

  constructor(private localization: LocalizationService,
              private changeDetector: ChangeDetectorRef) {
    super();
  }

  public handleNumpadDecimal(event: KeyboardEvent) {
    if (event.code === 'NumpadDecimal') {
      this.number += this.localization.numberFormat.decimal;
      this.onChange();
    }
  }

  public ngOnInit(): void {
    this.localization.changeNumberFormatEvent
      .pipe(
        takeUntil(this.componentDestroyed$)
      )
      .subscribe(() => {
        this.setMask();
        this.changeDetector.markForCheck();
        let number = this._number + '';
        if (this.formControl.pristine) {
          number = number.split('.').join(this.localization.numberFormat.decimal);
          number = number.split(',').join(this.localization.numberFormat.decimal);
        }
        this.number = this.getDecimalFormateNumber(number);
      });

    this.setMask();
  }

  public get number() {
    return this._number;
  }

  public set number(value) {
    this._number = value;
  }

  public get moduleNumber() {
    if (this.formControl.pristine) {
      return this.getDecimalFormateNumber(this._number);
    } else {
      return this.getDecimalFormateNumber(this.normalNumber);
    }
  }

  public set moduleNumber(value) {
    this._number = value;
  }

  public writeValue(value: any) {
    this.number = isPresent(value) ? value : '';
  }

  public registerOnChange(fn: any) {
    this.propagateChange = fn;
  }

  public registerOnTouched() {
  }

  validate(formControl: UntypedFormControl): ValidationErrors | any {
    this.formControl = formControl;
    return formControl.valid;
  }

  public onFocus() {
    this.focus.emit();
  }

  public onBlur() {
    this.checkMaxValues(this.input.nativeElement.value);
    this.blur.emit();
  }

  public onChange() {
    if (this.checkMaxValues()) {
      return;
    }

    this.propagateChange(this.getUnformattedNumber(this._number));
  }

  public isValid(): boolean {
    const isPristine = this.validOnPristine ? this.formControl.pristine : false;
    return this.readOnly || this.formControl.valid || isPristine;
  }

  public checkMaxValues(value: number = this._number): boolean {
    if (this.getUnformattedNumber(value) > this.maxValue) {
      setTimeout(() => {
        this.number = this.maxValue;
        this.onChange();
      });
      return true;
    }

    if (this.getUnformattedNumber(value) < this.minValue) {
      setTimeout(() => {
        this.number = this.minValue;
        this.onChange();
      });
      return true;
    }

    return false;
  }

  private getUnformattedNumber(number): number {
    let result: any = number + '';
    const separator = this.localization.numberFormat.separator;
    const decimal = this.localization.numberFormat.decimal;

    result = result.split(separator).join('');
    result = result.split(decimal).join('.');

    result = parseFloat(result);
    result = isPresent(result) && !isNaN(result) ? result : '';
    this.normalNumber = result;
    return result;
  }

  private getDecimalFormateNumber(number) {
    let result = number + '';
    if (this.type === 'decimal') {
      result = result.replace(/\./g, this.localization.numberFormat.decimal);
    }
    result = isPresent(result) ? result : '';
    return result;
  }

  private setMask() {
    switch (this.type) {
      case ('decimal'): {
        this.mask = this.localization.getDecimalNumberFormat(this.maxDigits);
        break;
      }
      case ('integer'): {
        this.mask = this.localization.numberIntegerMask;
        break;
      }
    }
  }

  private propagateChange = (_: any) => {
  }
}
